/**
 * Persists agenda UI context per tenant across studio switches.
 */
export const AGENDA_UNIT_BY_TENANT_KEY = "adatis.agenda.unitByTenant";
export const AGENDA_TAB_BY_TENANT_KEY = "adatis.agenda.tabByTenant";
export const AGENDA_CALENDAR_VIEW_BY_TENANT_KEY = "adatis.agenda.calendarViewByTenant";
export const AGENDA_PROFESSIONALS_BY_TENANT_KEY = "adatis.agenda.professionalsByTenant";
export const AGENDA_SHOW_INACTIVE_BY_TENANT_KEY = "adatis.agenda.showInactiveByTenant";
export const AGENDA_LIST_ONLY_EXTRAS_BY_TENANT_KEY =
  "adatis.agenda.listOnlyWithExtrasByTenant";
export const AGENDA_LIST_LATE_ONLY_BY_TENANT_KEY =
  "adatis.agenda.listLateOnlyByTenant";
export const AGENDA_LAST_OPTIONS_ACTION_BY_TENANT_KEY =
  "adatis.agenda.lastOptionsActionByTenant";

export type AgendaTabPreference = "calendar" | "list" | "waiting";
export type AgendaCalendarViewPreference = "day" | "week";
export type AgendaOptionsActionPreference =
  | "unit_schedule"
  | "guide"
  | "status"
  | "print"
  | "waitlist";

const AGENDA_OPTIONS_ACTIONS: AgendaOptionsActionPreference[] = [
  "unit_schedule",
  "guide",
  "status",
  "print",
  "waitlist",
];

type StringByTenantMap = Record<string, string>;

const readMap = (key: string): StringByTenantMap => {
  if (typeof window === "undefined") {
    return {};
  }

  try {
    const raw = localStorage.getItem(key);

    if (!raw) {
      return {};
    }

    const parsed = JSON.parse(raw) as unknown;

    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }

    return parsed as StringByTenantMap;
  } catch {
    return {};
  }
};

const writeMap = (key: string, map: StringByTenantMap): void => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    localStorage.setItem(key, JSON.stringify(map));
  } catch {
    // ignore quota / private mode
  }
};

const getMappedValue = (
  key: string,
  tenantId: number | null | undefined
): string | null => {
  if (tenantId == null || !Number.isFinite(tenantId)) {
    return null;
  }

  const value = readMap(key)[String(tenantId)];

  return typeof value === "string" ? value : null;
};

const setMappedValue = (
  key: string,
  tenantId: number | null | undefined,
  value: string
): void => {
  if (tenantId == null || !Number.isFinite(tenantId)) {
    return;
  }

  const map = readMap(key);
  map[String(tenantId)] = value;
  writeMap(key, map);
};

export const getAgendaUnitForTenant = (tenantId: number | null | undefined): string | null =>
  getMappedValue(AGENDA_UNIT_BY_TENANT_KEY, tenantId);

export const setAgendaUnitForTenant = (
  tenantId: number | null | undefined,
  unitId: string
): void => setMappedValue(AGENDA_UNIT_BY_TENANT_KEY, tenantId, unitId);

export const getAgendaTabForTenant = (
  tenantId: number | null | undefined
): AgendaTabPreference | null => {
  const value = getMappedValue(AGENDA_TAB_BY_TENANT_KEY, tenantId);

  return value === "list" || value === "calendar" || value === "waiting"
    ? value
    : null;
};

export const setAgendaTabForTenant = (
  tenantId: number | null | undefined,
  tab: AgendaTabPreference
): void => setMappedValue(AGENDA_TAB_BY_TENANT_KEY, tenantId, tab);

export const getAgendaCalendarViewForTenant = (
  tenantId: number | null | undefined
): AgendaCalendarViewPreference | null => {
  const value = getMappedValue(AGENDA_CALENDAR_VIEW_BY_TENANT_KEY, tenantId);

  return value === "week" || value === "day" ? value : null;
};

export const setAgendaCalendarViewForTenant = (
  tenantId: number | null | undefined,
  view: AgendaCalendarViewPreference
): void => setMappedValue(AGENDA_CALENDAR_VIEW_BY_TENANT_KEY, tenantId, view);

export const getAgendaProfessionalIdsForTenant = (
  tenantId: number | null | undefined
): number[] | null => {
  const raw = getMappedValue(AGENDA_PROFESSIONALS_BY_TENANT_KEY, tenantId);

  if (raw == null) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as unknown;

    if (!Array.isArray(parsed)) {
      return null;
    }

    return parsed.map(Number).filter((value) => Number.isFinite(value));
  } catch {
    return null;
  }
};

export const setAgendaProfessionalIdsForTenant = (
  tenantId: number | null | undefined,
  professionalIds: number[]
): void =>
  setMappedValue(
    AGENDA_PROFESSIONALS_BY_TENANT_KEY,
    tenantId,
    JSON.stringify(professionalIds)
  );

export const getAgendaShowInactiveForTenant = (
  tenantId: number | null | undefined
): boolean | null => {
  const value = getMappedValue(AGENDA_SHOW_INACTIVE_BY_TENANT_KEY, tenantId);

  if (value === "true") {
    return true;
  }

  if (value === "false") {
    return false;
  }

  return null;
};

export const setAgendaShowInactiveForTenant = (
  tenantId: number | null | undefined,
  showInactive: boolean
): void =>
  setMappedValue(
    AGENDA_SHOW_INACTIVE_BY_TENANT_KEY,
    tenantId,
    showInactive ? "true" : "false"
  );

export const getAgendaListOnlyWithExtrasForTenant = (
  tenantId: number | null | undefined
): boolean | null => {
  const value = getMappedValue(AGENDA_LIST_ONLY_EXTRAS_BY_TENANT_KEY, tenantId);

  if (value === "true") {
    return true;
  }

  if (value === "false") {
    return false;
  }

  return null;
};

export const setAgendaListOnlyWithExtrasForTenant = (
  tenantId: number | null | undefined,
  onlyWithExtras: boolean
): void =>
  setMappedValue(
    AGENDA_LIST_ONLY_EXTRAS_BY_TENANT_KEY,
    tenantId,
    onlyWithExtras ? "true" : "false"
  );

export const getAgendaListLateOnlyForTenant = (
  tenantId: number | null | undefined
): boolean | null => {
  const value = getMappedValue(AGENDA_LIST_LATE_ONLY_BY_TENANT_KEY, tenantId);

  if (value === "true") {
    return true;
  }

  if (value === "false") {
    return false;
  }

  return null;
};

export const setAgendaListLateOnlyForTenant = (
  tenantId: number | null | undefined,
  lateOnly: boolean
): void =>
  setMappedValue(
    AGENDA_LIST_LATE_ONLY_BY_TENANT_KEY,
    tenantId,
    lateOnly ? "true" : "false"
  );

export const getAgendaLastOptionsActionForTenant = (
  tenantId: number | null | undefined
): AgendaOptionsActionPreference | null => {
  const value = getMappedValue(AGENDA_LAST_OPTIONS_ACTION_BY_TENANT_KEY, tenantId);

  return AGENDA_OPTIONS_ACTIONS.includes(value as AgendaOptionsActionPreference)
    ? (value as AgendaOptionsActionPreference)
    : null;
};

export const setAgendaLastOptionsActionForTenant = (
  tenantId: number | null | undefined,
  action: AgendaOptionsActionPreference
): void => setMappedValue(AGENDA_LAST_OPTIONS_ACTION_BY_TENANT_KEY, tenantId, action);

export const resolveCurrentTenantId = (): number | null => {
  try {
    const raw = sessionStorage.getItem("authUser");

    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as { user?: Record<string, unknown> | null };
    const tenantId = parsed.user?.tenant_id;

    if (typeof tenantId === "number" && Number.isFinite(tenantId)) {
      return tenantId;
    }

    if (typeof tenantId === "string" && tenantId.trim() !== "") {
      const numeric = Number(tenantId);

      return Number.isFinite(numeric) ? numeric : null;
    }

    return null;
  } catch {
    return null;
  }
};
